import { Info, Download, Calendar, TrendingUp, DollarSign, Percent, BarChart3, Landmark } from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { CHART_TOOLTIP_PROPS, chartAxisProps, chartGridProps, chartSeriesColor } from '@/config/chartTheme'
import { useModuleDemoEnabled } from '@/hooks/useDemoDataAccess'

// ── Portfolio NAV Time Series ────────────────────────────────────────────
const navData = [
  { quarter: "Q1'24", nav: 180.2 },
  { quarter: "Q2'24", nav: 188.5 },
  { quarter: "Q3'24", nav: 196.1 },
  { quarter: "Q4'24", nav: 204.8 },
  { quarter: "Q1'25", nav: 215.3 },
  { quarter: "Q2'25", nav: 224.7 },
  { quarter: "Q3'25", nav: 236.2 },
  { quarter: "Q4'25", nav: 245.1 },
]

// ── Distribution History ─────────────────────────────────────────────────
const distributions = [
  { quarter: "Q4 2025", amount: 3_850_000, type: 'Income', yield: '6.3%' },
  { quarter: "Q3 2025", amount: 3_720_000, type: 'Income', yield: '6.1%' },
  { quarter: "Q2 2025", amount: 4_100_000, type: 'Return of Capital', yield: '6.7%' },
  { quarter: "Q1 2025", amount: 3_560_000, type: 'Income', yield: '5.8%' },
  { quarter: "Q4 2024", amount: 3_410_000, type: 'Income', yield: '5.6%' },
  { quarter: "Q3 2024", amount: 3_680_000, type: 'Return of Capital', yield: '6.0%' },
]

// ── Investment Summary ───────────────────────────────────────────────────
const investments = [
  { property: 'Harbor Point Residences', type: 'Multifamily', date: '2022-06-15', invested: 18_500_000, current: 24_200_000, gain: 5_700_000, irr: 16.8, multiple: 1.52, distribution: 2_960_000, status: 'Active' },
  { property: 'Pinnacle Tower', type: 'Office', date: '2021-11-01', invested: 22_000_000, current: 26_800_000, gain: 4_800_000, irr: 12.4, multiple: 1.38, distribution: 3_520_000, status: 'Active' },
  { property: 'Ridge Logistics Center', type: 'Industrial', date: '2023-02-20', invested: 15_000_000, current: 19_500_000, gain: 4_500_000, irr: 17.2, multiple: 1.44, distribution: 1_800_000, status: 'Active' },
  { property: 'Meridian Apartments', type: 'Multifamily', date: '2022-09-10', invested: 12_500_000, current: 15_900_000, gain: 3_400_000, irr: 14.1, multiple: 1.40, distribution: 2_000_000, status: 'Active' },
  { property: 'Union Retail Galleria', type: 'Retail', date: '2021-04-22', invested: 25_000_000, current: 28_100_000, gain: 3_100_000, irr: 8.6, multiple: 1.26, distribution: 4_000_000, status: 'Active' },
  { property: 'Canal Street Labs', type: 'Lab/R&D', date: '2023-08-05', invested: 30_000_000, current: 38_400_000, gain: 8_400_000, irr: 18.3, multiple: 1.56, distribution: 3_600_000, status: 'Active' },
  { property: 'Seaport Mixed-Use', type: 'Mixed-Use', date: '2024-01-15', invested: 20_000_000, current: 23_600_000, gain: 3_600_000, irr: 13.9, multiple: 1.32, distribution: 1_600_000, status: 'Active' },
  { property: 'Copperline Distribution', type: 'Industrial', date: '2022-03-30', invested: 14_000_000, current: 17_800_000, gain: 3_800_000, irr: 15.7, multiple: 1.48, distribution: 2_240_000, status: 'Active' },
  { property: 'Bay Harbor Hotel', type: 'Hospitality', date: '2020-12-01', invested: 16_000_000, current: 14_200_000, gain: -1_800_000, irr: -2.4, multiple: 0.96, distribution: 960_000, status: 'Watch' },
  { property: 'Atlantic Medical Office', type: 'Office', date: '2023-05-18', invested: 11_000_000, current: 12_100_000, gain: 1_100_000, irr: 9.8, multiple: 1.16, distribution: 880_000, status: 'Active' },
]

// ── Quarterly Reports ────────────────────────────────────────────────────
const reports = [
  { name: 'Q4 2025 Investor Report', quarter: 'Q4 2025', date: '2026-01-28', pages: 42 },
  { name: 'Q3 2025 Investor Report', quarter: 'Q3 2025', date: '2025-10-30', pages: 38 },
  { name: 'Q2 2025 Investor Report', quarter: 'Q2 2025', date: '2025-07-29', pages: 40 },
  { name: 'Q1 2025 Investor Report', quarter: 'Q1 2025', date: '2025-04-28', pages: 36 },
]

// ── Upcoming Events ──────────────────────────────────────────────────────
const events = [
  { name: 'Q1 Distribution', date: 'March 31, 2026', icon: DollarSign, tone: 'text-emerald-400' },
  { name: 'Capital Call #3', date: 'April 1, 2026', icon: Landmark, tone: 'text-amber-300' },
  { name: 'Annual Meeting', date: 'April 15, 2026', icon: Calendar, tone: 'text-amber-300' },
]

// ── Metrics ──────────────────────────────────────────────────────────────
const metrics = [
  { label: 'Total Investment', value: '$184.0M', icon: DollarSign },
  { label: 'Current Value', value: '$220.6M', icon: TrendingUp },
  { label: 'Total Return', value: '+$36.6M', icon: BarChart3 },
  { label: 'Distributions YTD', value: '$15.2M', icon: DollarSign },
  { label: 'Weighted IRR', value: '13.4%', icon: Percent },
  { label: 'Wtd Equity Multiple', value: '1.37x', icon: TrendingUp },
]

function fmt(n: number) {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  return `$${n.toFixed(0)}`
}

/**
 * The arrays above are an invented LP portfolio ($184M invested, 13.4% IRR,
 * distributions, "Q4 2025 Investor Report" downloads, past-dated events) —
 * and one holding is named like a live pipeline deal (Pinnacle Tower). It
 * rendered on live accounts as a "preview" with nothing saying the figures
 * were sample data (MOD-04). Live accounts now get a plain statement of what
 * the portal needs; the preview is demo-only and labelled as sample data.
 */
export default function InvestorPortal() {
  const demoEnabled = useModuleDemoEnabled('investor-portal')
  return demoEnabled ? <InvestorPortalSample /> : <InvestorPortalPending />
}

function InvestorPortalPending() {
  return (
    <div className="space-y-3 p-4">
      <section className="card px-5 py-4">
        <div className="flex items-center gap-2">
          <Landmark size={14} className="text-amber-400" />
          <span className="terminal-kicker">Investor Portal</span>
        </div>
        <p className="mt-2 max-w-3xl text-[12px] leading-relaxed text-stone-400">
          No investor or LP records exist on this account, so there is no portfolio, NAV, distribution, report or
          event to show. The portal fills from recorded fund investments, capital calls and distributions once those
          are captured; nothing here is estimated in the meantime.
        </p>
      </section>
    </div>
  )
}

function InvestorPortalSample() {
  return (
    <div className="page-fill gap-2 p-3">
      {/* Banner */}
      <div className="flex flex-shrink-0 items-center gap-2.5 rounded-xl border border-amber-400/15 bg-amber-400/[0.04] px-4 py-1.5">
        <Info size={14} className="flex-shrink-0 text-amber-300" />
        <span className="text-xs text-amber-200/80">
          Sample data — an illustrative LP portfolio showing how investors would see performance. None of these
          holdings, figures or reports are real.
        </span>
      </div>

      {/* Metric Strip */}
      <section className="grid flex-shrink-0 gap-2 md:grid-cols-3 xl:grid-cols-6">
        {metrics.map((m) => (
          <div key={m.label} className="card px-4 py-3">
            <div className="flex items-center gap-1.5">
              <m.icon size={11} className="text-stone-600" />
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-600">{m.label}</div>
            </div>
            <div className="mt-1 text-lg font-semibold text-stone-100">{m.value}</div>
          </div>
        ))}
      </section>

      {/* Top Section: NAV Chart + Distribution History */}
      <section className="grid min-h-0 min-h-[180px] flex-[0.85] gap-2 overflow-hidden xl:grid-cols-[1.2fr_0.8fr]">
        {/* NAV Chart */}
        <div className="card flex min-h-0 flex-col overflow-hidden px-4 py-3">
          <div className="terminal-kicker">Portfolio NAV Over Time</div>
          <p className="mt-1 text-[11px] text-stone-500">Net Asset Value ($M) across 8 quarters</p>
          <div className="mt-2 min-h-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={navData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid {...chartGridProps()} />
                <XAxis dataKey="quarter" {...chartAxisProps()} />
                <YAxis domain={[170, 260]} {...chartAxisProps()} tickFormatter={(v) => `$${v}M`} />
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(value: number) => [`$${value.toFixed(1)}M`, 'NAV']} />
                <Line type="monotone" dataKey="nav" stroke={chartSeriesColor(0)} strokeWidth={2.5} dot={{ fill: chartSeriesColor(0), r: 4, strokeWidth: 0 }} activeDot={{ r: 6, fill: chartSeriesColor(5) }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Distribution History */}
        <div className="card overflow-hidden">
          <div className="border-b border-amber-400/10 px-4 py-3">
            <div className="terminal-kicker">Distribution History</div>
            <p className="mt-1 text-[11px] text-stone-500">Quarterly distributions to LPs</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-amber-400/10">
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium text-stone-600">Quarter</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-medium text-stone-600">Amount</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium text-stone-600">Type</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-medium text-stone-600">Yield</th>
                </tr>
              </thead>
              <tbody>
                {distributions.map((d) => (
                  <tr key={d.quarter} className="border-b border-amber-400/6 hover:bg-white/[0.02]">
                    <td className="px-4 py-2.5 text-xs text-stone-300">{d.quarter}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-stone-200">{fmt(d.amount)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] ${d.type === 'Income' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-amber-400/30 bg-amber-400/10 text-amber-200'}`}>
                        {d.type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-emerald-400">{d.yield}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Investment Summary Table */}
      <section className="card flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-shrink-0 border-b border-amber-400/10 px-4 py-2">
          <div className="terminal-kicker">Investment Summary</div>
          <p className="mt-0.5 text-[11px] text-stone-500">Full portfolio holdings with performance attribution</p>
        </div>
        <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-amber-400/10">
                {['Property', 'Type', 'Inv. Date', 'Invested', 'Current Value', 'Unrealized Gain', 'IRR', 'Multiple', 'Distribution', 'Status'].map((col) => (
                  <th key={col} className="whitespace-nowrap px-3 py-2.5 text-left text-[10px] font-medium text-stone-600">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {investments.map((inv) => (
                <tr key={inv.property} className="border-b border-amber-400/6 hover:bg-white/[0.02]">
                  <td className="whitespace-nowrap px-3 py-2.5 text-xs font-medium text-amber-300">{inv.property}</td>
                  <td className="px-3 py-2.5 text-xs text-stone-400">{inv.type}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-stone-400">{inv.date}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-stone-200">{fmt(inv.invested)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-stone-200">{fmt(inv.current)}</td>
                  <td className={`px-3 py-2.5 text-right font-mono text-xs ${inv.gain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{inv.gain >= 0 ? '+' : ''}{fmt(inv.gain)}</td>
                  <td className={`px-3 py-2.5 text-right font-mono text-xs ${inv.irr >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{inv.irr.toFixed(1)}%</td>
                  <td className={`px-3 py-2.5 text-right font-mono text-xs ${inv.multiple >= 1 ? 'text-stone-200' : 'text-red-400'}`}>{inv.multiple.toFixed(2)}x</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-stone-300">{fmt(inv.distribution)}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] ${inv.status === 'Active' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-red-500/30 bg-red-500/10 text-red-300'}`}>
                      {inv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-amber-400/20 bg-amber-400/[0.03]">
                <td className="px-3 py-2.5 text-xs font-semibold text-stone-100" colSpan={3}>Total</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-stone-100">{fmt(investments.reduce((s, i) => s + i.invested, 0))}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-stone-100">{fmt(investments.reduce((s, i) => s + i.current, 0))}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-emerald-400">+{fmt(investments.reduce((s, i) => s + i.gain, 0))}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-stone-100">13.4%</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-stone-100">1.37x</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-stone-100">{fmt(investments.reduce((s, i) => s + i.distribution, 0))}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* Bottom: Report Archive + Upcoming Events */}
      <section className="grid min-h-0 flex-[0.7] gap-2 overflow-hidden xl:grid-cols-[1.2fr_0.8fr]">
        <div className="card overflow-hidden">
          <div className="border-b border-amber-400/10 px-4 py-3">
            <div className="terminal-kicker">Quarterly Report Archive</div>
            <p className="mt-1 text-[11px] text-stone-500">Downloadable investor packages</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-amber-400/10">
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium text-stone-600">Report</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium text-stone-600">Quarter</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium text-stone-600">Date</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-medium text-stone-600">Pages</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-medium text-stone-600">Download</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.quarter} className="border-b border-amber-400/6 hover:bg-white/[0.02]">
                    <td className="px-4 py-2.5 text-xs font-medium text-stone-200">{r.name}</td>
                    <td className="px-4 py-2.5 text-xs text-stone-400">{r.quarter}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-stone-400">{r.date}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-stone-400">{r.pages}</td>
                    <td className="px-4 py-2.5 text-center">
                      <button className="inline-flex items-center gap-1 rounded-lg border border-amber-400/20 bg-amber-400/[0.06] px-2.5 py-1 text-[10px] font-medium text-amber-200 transition-colors hover:bg-amber-400/[0.12]">
                        <Download size={10} /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="card px-4 py-4">
          <div className="terminal-kicker">Upcoming Events</div>
          <p className="mt-1 text-[11px] text-stone-500">Key dates for investors and LPs</p>
          <div className="mt-4 space-y-3">
            {events.map((e) => (
              <div key={e.name} className="flex items-center gap-3 rounded-xl border border-amber-400/8 bg-white/[0.015] px-4 py-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-amber-400/15 bg-amber-400/[0.06]">
                  <e.icon size={16} className={e.tone} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-stone-100">{e.name}</div>
                  <div className="mt-0.5 font-mono text-xs text-stone-500">{e.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
